import { useCallback, useState } from 'react';

import { localPoint } from '@visx/event';
import { useTooltip } from '@visx/tooltip';
import { bisector } from 'd3-array';
import { ScaleLinear, ScaleTime } from 'd3-scale';

export type ChartHighlightData<T> = { highlight: NonNullable<T>; highlightLeft: number | undefined };

export type TimeDomainScale = { scale: ScaleTime<number, number>; type: Date };
export type IndexDomainScale = { scale: ScaleLinear<number, number>; type: number };
type DomainScale = TimeDomainScale | IndexDomainScale;

type Props<T, D extends DomainScale> = {
  dateScale: D['scale'];
  data: NonNullable<T>[];
  xAccessor: (d: NonNullable<T>) => D['type'];
  disabled?: boolean;
  marginLeft: number;
  onHighlightChange?: (data: NonNullable<T> | null) => void;
};

export const useChartHighlight = <T, D extends DomainScale>(props: Props<T, D>) => {
  const { dateScale, data, disabled, xAccessor, marginLeft, onHighlightChange } = props;

  const { tooltipData: initialHighlight, tooltipLeft, hideTooltip } = useTooltip<T>();
  const [highlightData, _setCurrentHighlightData] = useState<ChartHighlightData<T> | null>(
    initialHighlight ? { highlight: initialHighlight, highlightLeft: tooltipLeft } : null
  );

  const setCurrentHighlightData = useCallback(
    (newHighlight: ChartHighlightData<T> | null) => {
      _setCurrentHighlightData(newHighlight);
      onHighlightChange?.(newHighlight ? newHighlight.highlight : null);
    },
    [onHighlightChange, _setCurrentHighlightData]
  );

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const bisectDate = bisector<NonNullable<T>, DomainScale['type']>(d => {
    const val = xAccessor(d);
    return val instanceof Date ? val : new Date(val);
  }).left;

  const xDate = (x: DomainScale['type']) => (x instanceof Date ? x : new Date(x));

  const handleHighlightUpdate = useCallback(
    (event: React.TouchEvent<SVGRectElement> | React.MouseEvent<SVGRectElement>) => {
      if (disabled) return;

      const { x: xInitial } = localPoint(event) || { x: 0 };
      const xPosition = xInitial - marginLeft;
      const xDomainPoint = dateScale.invert(xPosition);

      let highlight;

      if (xDomainPoint instanceof Date) {
        // Using time scale
        const xDomainTime = xDomainPoint.getTime();

        const indexRight = bisectDate(data, xDomainPoint, 1);
        const indexLeft = indexRight - 1;

        const pointLeft = data[indexLeft];
        const pointRight = data[indexRight];

        const timeLeft = xDate(xAccessor(data[indexLeft])).getTime();
        const timeRight = xDate(xAccessor(data[indexRight])).getTime();

        const distanceToLeft = Math.abs(xDomainTime - timeLeft);
        const distanceToRight = Math.abs(timeRight - xDomainTime);

        highlight = distanceToLeft < distanceToRight ? pointLeft : pointRight;
      } else {
        // Using index scale
        highlight = data[Math.round(xDomainPoint)];
      }
      setCurrentHighlightData({ highlight, highlightLeft: xPosition });
    },
    [marginLeft, dateScale, bisectDate, data, xAccessor, disabled, setCurrentHighlightData]
  );

  const handleHighlightCancel = useCallback(() => {
    hideTooltip();
    setCurrentHighlightData(null);
  }, [hideTooltip, setCurrentHighlightData]);

  return { highlightData, handleHighlightUpdate, handleHighlightCancel };
};

export function clamp({ number, min, max }: { number: number; min: number; max: number }) {
  return Math.max(min, Math.min(number, max));
}
